import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import * as labels from "config.labels";
import { formatCession } from "domain/cession";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const optionLabel = (option) =>
  typeof option === "string" ? option : option?.name ?? "";

// A type-ahead that still accepts free text. The stored value is always the
// display name (an option's `name`, or whatever was typed), never the option
// key, so a pick from the list and a hand-typed entry read the same in the
// submission and in reports.
const NameAutocomplete = ({ id, label, value, options, loading, onChange }) => (
  <Disabled ifReadOnly>
    <Autocomplete
      freeSolo
      value={value ?? ""}
      inputValue={value ?? ""}
      onChange={(_, selected) => onChange(optionLabel(selected) || null)}
      onInputChange={(_, text, reason) => {
        if (reason === "input" || reason === "clear") {
          onChange(text || null);
        }
      }}
      options={options}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, selected) =>
        optionLabel(option) === optionLabel(selected)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant={"filled"}
          label={label}
          InputLabelProps={{ shrink: true }}
          autoComplete={"off"}
        />
      )}
      autoHighlight
      sx={{ width: "100%" }}
      disabled={loading}
      id={id}
    />
  </Disabled>
);

const Cession = () => {
  const dispatch = useDispatch();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const program = useSelector(programSelectors.selectResolvedProgram);
  const { data: autoCompletions, isLoading } = staticDataService.useConfigQuery(
    "auto_completions"
  );

  const reinsureds = autoCompletions?.input_program_reinsured ?? [];
  const mgas = autoCompletions?.input_program_mga ?? [];

  const update = (values) => dispatch(programActions.updateCession(values));

  const caption = formatCession(
    program?.cessionLimitFrom,
    program?.cessionLimitTo
  );

  return (
    <>
      <Grid item xs={6}>
        <NameAutocomplete
          id={"program-inputs-reinsured"}
          label={translateLabel(labels.REINSURED, {
            default: "Reinsured / Fronting Carrier",
          })}
          value={program?.reinsured}
          options={reinsureds}
          loading={isLoading}
          onChange={(reinsured) => update({ reinsured })}
        />
      </Grid>
      <Grid item xs={6}>
        <NameAutocomplete
          id={"program-inputs-mga"}
          label={translateLabel(labels.MGA, { default: "MGA" })}
          value={program?.mga}
          options={mgas}
          loading={isLoading}
          onChange={(mga) => update({ mga })}
        />
      </Grid>
      <Grid item xs={6}>
        <Disabled ifReadOnly>
          <PrettyNumberTextField
            InputLabelProps={{ shrink: true }}
            label={translateLabel(labels.CESSION_LIMIT_FROM, {
              default: "Cession From",
            })}
            value={program?.cessionLimitFrom ?? ""}
            onChangeNumber={(cessionLimitFrom) => update({ cessionLimitFrom })}
            variant={"filled"}
            fullWidth
          />
        </Disabled>
      </Grid>
      <Grid item xs={6}>
        <Disabled ifReadOnly>
          <PrettyNumberTextField
            InputLabelProps={{ shrink: true }}
            label={translateLabel(labels.CESSION_LIMIT_TO, {
              default: "Cession To",
            })}
            value={program?.cessionLimitTo ?? ""}
            onChangeNumber={(cessionLimitTo) => update({ cessionLimitTo })}
            variant={"filled"}
            fullWidth
          />
        </Disabled>
      </Grid>
      {caption && (
        <Grid item xs={12}>
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            data-testid={"program-inputs-cession-caption"}
          >
            {caption}
          </Typography>
        </Grid>
      )}
    </>
  );
};

export default Cession;
